// src/pages/Orders/BulkOrderImport.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import useCustomerPricing from '../../hooks/useCustomerPricing';

const styles = {
    container: {
        fontFamily: '"Inter", sans-serif',
        padding: '24px',
        maxWidth: '1200px',
        margin: '0 auto',
        color: '#1e293b'
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        padding: '24px',
        marginBottom: '24px'
    },
    title: { fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: '#0f172a' },
    dropZone: {
        border: '2px dashed #cbd5e1',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
        background: '#f8fafc',
        marginBottom: '24px'
    },
    dropZoneActive: {
        borderColor: '#3b82f6',
        background: '#eff6ff'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '13px',
        marginTop: '20px'
    },
    th: {
        background: '#f8fafc',
        padding: '12px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        color: '#475569'
    },
    td: {
        padding: '12px',
        borderBottom: '1px solid #e2e8f0',
        verticalAlign: 'middle'
    },
    badge: {
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '600'
    },
    badgeSuccess: { background: '#dcfce7', color: '#166534' },
    badgeError: { background: '#fee2e2', color: '#991b1b' },
    button: {
        padding: '10px 20px',
        borderRadius: '8px',
        border: 'none',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px'
    },
    primaryButton: { 
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)', 
        color: 'white',
        boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)'
    },
    disabledButton: { background: '#cbd5e1', color: '#64748b', cursor: 'not-allowed', boxShadow: 'none' },
    formGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        background: 'white',
        boxSizing: 'border-box'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        boxSizing: 'border-box'
    },
    grid2Col: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        alignItems: 'end',
        marginBottom: '20px'
    },
    suggestions: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        maxHeight: '200px',
        overflowY: 'auto',
        zIndex: 10,
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    },
    suggestionItem: {
        padding: '10px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#334155',
        transition: 'background 0.1s'
    }
};

function BulkOrderImport() {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const { getEffectivePrice } = useCustomerPricing(userData?.companyId);

    const [fileData, setFileData] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [processedItems, setProcessedItems] = useState([]);
    const [importStatus, setImportStatus] = useState('config'); // config, preview, processing, completed

    // Configuration states
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [customerNameInput, setCustomerNameInput] = useState('');
    const [vatRate, setVatRate] = useState(20);

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;

        // Fetch customers and products
        const unsubCustomers = onSnapshot(collection(db, 'companies', companyId, 'customers'), (snap) => {
            setCustomers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubProducts = onSnapshot(collection(db, 'companies', companyId, 'products'), (snap) => {
            setProducts(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => { unsubCustomers(); unsubProducts(); };
    }, [userData]);

    const handleCustomerSelect = (customer) => {
        setCustomerNameInput(customer.name);
        setSelectedCustomerId(customer.id);
    };

    const handleFileUpload = (e) => {
        if (!selectedCustomerId) {
            setError('Lütfen önce bir müşteri seçin.');
            return;
        }
        const file = e.target.files?.[0];
        if (!file) return;
        processFile(file);
    };

    const processFile = (file) => {
        setError(null);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);
                validateAndPrepare(data);
            } catch (err) {
                setError("Dosya okunurken hata oluştu: " + err.message);
            }
        };
        reader.readAsBinaryString(file);
    };

    const validateAndPrepare = (data) => {
        const items = [];
        const customer = customers.find(c => c.id === selectedCustomerId);

        if (!customer) {
            setError("Seçili müşteri bulunamadı.");
            return;
        }

        data.forEach((row, index) => {
            // Expected columns: Ürün Adı, Miktar, Birim Fiyat, Not
            const productName = row['Ürün Adı'] || row['Ürün'] || row['Product'];
            const quantity = Number(row['Miktar'] || row['Adet'] || row['Quantity'] || 0);
            const manualPrice = row['Birim Fiyat'] || row['Fiyat'] || row['Price'];
            const note = row['Not'] || row['Açıklama'] || '';

            const product = products.find(p => p.name.toLowerCase() === productName?.toString().toLowerCase() || p.barcode === productName?.toString());

            const errors = [];
            if (!product) errors.push("Katalogda Ürün Bulunamadı");
            if (quantity <= 0) errors.push("Miktar > 0 olmalı");

            let finalPrice = 0;
            let priceLabel = '';

            if (product) {
                if (manualPrice !== undefined && manualPrice !== null && manualPrice !== '') {
                    finalPrice = Number(manualPrice);
                    priceLabel = 'Excel Fiyatı';
                } else {
                    // Use effective price hook calculating best possible discount mapping
                    const effective = getEffectivePrice(customer.id, product.id, product, customer.customerGroupId);
                    finalPrice = effective.price;
                    priceLabel = effective.label || 'Katalog Fiyatı';
                }
            }

            items.push({
                rowId: index,
                productName: productName || 'Belirtilmedi',
                productId: product?.id,
                product: product, // keep full ref
                quantity,
                price: finalPrice,
                priceLabel,
                note,
                status: errors.length > 0 ? 'error' : 'valid',
                errors,
                total: finalPrice * quantity
            });
        });

        setProcessedItems(items);
        setImportStatus('preview');
    };

    const handleImport = async () => {
        const validItems = processedItems.filter(i => i.status === 'valid');
        if (validItems.length === 0) return;

        setLoading(true);
        const companyId = userData.companyId;

        try {
            const customer = customers.find(c => c.id === selectedCustomerId);
            const newOrderRef = doc(collection(db, 'companies', companyId, 'orders'));

            const lineItems = validItems.map(o => ({
                productId: o.productId,
                quantity: o.quantity,
                price: o.price,
                total: o.total,
                vatRate: Number(vatRate) / 100, // item vat matched to general order preference for bulk
                vatAmount: o.total * (Number(vatRate) / 100)
            }));

            const subTotal = lineItems.reduce((s, i) => s + i.total, 0);
            const vatFactor = Number(vatRate) / 100;
            const vatAmount = subTotal * vatFactor;

            const orderData = {
                customerId: customer.id,
                customerName: customer.name,
                customerPhone: customer.phone || '',
                customerEmail: customer.email || '',
                orderDate: new Date(),
                lineItems,
                subTotal,
                vatRate: vatFactor,
                vatAmount: vatAmount,
                discountRate: 0,
                discountAmount: 0,
                discountedSubTotal: subTotal,
                shippingCost: 0,
                total: subTotal + vatAmount,
                currency: customer.currency || 'TRY',
                status: 'Sipariş Alındı',
                note: validItems.map(o => o.note).filter(n => n).join(' | '),
                orderNumber: newOrderRef.id.substring(0, 6).toUpperCase(),
                isBulkImport: true, // indicates it was generated via excel logic natively
                createdAt: serverTimestamp()
            };

            await setDoc(newOrderRef, orderData);
            setImportStatus('completed');
            alert(`Sipariş başarıyla oluşturuldu (${validItems.length} ürün eklendi).`);
            navigate('/siparisler/' + newOrderRef.id);
        } catch (err) {
            console.error(err);
            setError("İçe aktarma sırasında hata: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const downloadSampleExcel = () => {
        const sampleData = [
            {
                'Ürün Adı': 'Örnek Ürün',
                'Miktar': 10,
                'Birim Fiyat': 150.00,
                'Not': 'Birim Fiyatı silerseniz sistem müşterinin indirimli/özel fiyatını çeker'
            },
            {
                'Ürün Adı': 'Mutfak Dolabı',
                'Miktar': 2,
                'Birim Fiyat': '',
                'Not': 'Acil'
            }
        ];

        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Satirlar");

        // Auto-size columns
        const maxWidths = {
            'Ürün Adı': 30,
            'Miktar': 10,
            'Birim Fiyat': 15,
            'Not': 40
        };
        worksheet['!cols'] = Object.keys(maxWidths).map(key => ({ wch: maxWidths[key] }));

        XLSX.writeFile(workbook, "Hizli_Siparis_Taslak.xlsx");
    };

    const filteredCustomers = customerNameInput ? customers.filter(c => c.name.toLowerCase().includes(customerNameInput.toLowerCase())) : [];

    return (
        <div style={styles.container}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h1 style={styles.title}><span role="img" aria-label="lightning">⚡</span> Hızlı Excel Siparişi</h1>
                    <p style={{ color: '#64748b', fontSize: '15px' }}>
                        Tek bir müşteri için çok kalemli siparişi Excel dosyanızdan anında yükleyin.
                    </p>
                </div>
                <Link to="/siparisler" style={{ textDecoration: 'none', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Siparişlere Dön
                </Link>
            </div>

            <div style={styles.card}>
                {importStatus === 'config' && (
                    <div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                            <span role="img" aria-label="1">1️⃣</span> Sipariş Parametreleri ve Dosya Seçimi
                        </h3>

                        <div style={styles.grid2Col}>
                            <div style={{ ...styles.formGroup, position: 'relative' }}>
                                <label style={styles.label}>Müşteri Ara ve Seç *</label>
                                <input
                                    type="text"
                                    placeholder="Müşteri adı yazın..."
                                    style={styles.input}
                                    value={customerNameInput}
                                    onChange={e => { setCustomerNameInput(e.target.value); setSelectedCustomerId(''); }}
                                />
                                {filteredCustomers.length > 0 && customerNameInput && !selectedCustomerId && (
                                    <div style={styles.suggestions}>
                                        {filteredCustomers.map(c => (
                                            <div 
                                                key={c.id} 
                                                style={styles.suggestionItem} 
                                                onMouseDown={() => handleCustomerSelect(c)}
                                            >
                                                {c.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>KDV Oranı (%) *</label>
                                <input
                                    type="number"
                                    style={styles.input}
                                    value={vatRate}
                                    onChange={(e) => setVatRate(e.target.value)}
                                    min="0"
                                    max="100"
                                />
                            </div>
                        </div>

                        {selectedCustomerId && (
                            <>
                                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
                                    Dosyanızda şu başlıklar bulunmalıdır: <strong>Ürün Adı, Miktar, Birim Fiyat (Opsiyonel), Not (Opsiyonel)</strong>. <br/>
                                    Eğer Birim Fiyat alanını boş bırakırsanız, sistem bu müşteri için geçerli olan (Varsa özel, yoksa grup, yoksa standart) fiyatı bulur.
                                </p>
                                <button
                                    onClick={downloadSampleExcel}
                                    style={{
                                        ...styles.button,
                                        background: '#f8fafc',
                                        color: '#334155',
                                        border: '1px solid #cbd5e1',
                                        marginBottom: '20px',
                                        fontSize: '13px'
                                    }}
                                >
                                    <span role="img" aria-label="download">📥</span> Örnek Excel Taslağı İndir
                                </button>

                                <div
                                    style={{
                                        ...styles.dropZone,
                                        ...(isDragging ? styles.dropZoneActive : {})
                                    }}
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                    onDragLeave={() => setIsDragging(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setIsDragging(false);
                                        const file = e.dataTransfer.files[0];
                                        if (file) processFile(file);
                                    }}
                                    onClick={() => document.getElementById('fileInput').click()}
                                >
                                    <span style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}>📁</span>
                                    <p style={{ margin: '0 0 8px 0', fontWeight: '600', fontSize: '16px', color: '#1e293b' }}>
                                        Dosyayı buraya sürükleyin veya tıklayarak seçin
                                    </p>
                                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>(.xlsx, .xls, .csv)</p>
                                    <input type="file" id="fileInput" hidden onChange={handleFileUpload} accept=".xlsx, .xls, .csv" />
                                </div>
                            </>
                        )}
                        {!selectedCustomerId && (
                            <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '12px', textAlign: 'center', color: '#64748b', border: '1px dashed #cbd5e1' }}>
                                Dosya yükleyebilmek için lütfen önce yukarıdan bir müşteri seçin.
                            </div>
                        )}
                    </div>
                )}

                {importStatus === 'preview' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#0f172a', margin: 0 }}>
                                <span role="img" aria-label="2">2️⃣</span> Satır Önizleme & Onay
                            </h3>
                            <div style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                                Seçili Müşteri: <span style={{ color: '#2563eb', fontWeight: '700' }}>{customers.find(c => c.id === selectedCustomerId)?.name}</span>
                            </div>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Bkz.</th>
                                        <th style={styles.th}>Ürün Sütunu</th>
                                        <th style={styles.th}>Algılanan Ürün / Miktar</th>
                                        <th style={styles.th}>Birim Fiyat</th>
                                        <th style={styles.th}>Toplam / Not</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedItems.map((item, idx) => (
                                        <tr key={idx} style={{ background: item.status === 'error' ? '#fff1f2' : 'transparent' }}>
                                            <td style={styles.td}>Satır {item.rowId + 2}</td>
                                            <td style={styles.td}>
                                                <div style={{ fontWeight: '500' }}>{item.productName}</div>
                                                {item.status === 'valid' ? (
                                                    <span style={{ ...styles.badge, ...styles.badgeSuccess, marginTop: '4px', display: 'inline-block' }}>Eşleşti</span>
                                                ) : (
                                                    <span style={{ ...styles.badge, ...styles.badgeError, marginTop: '4px', display: 'inline-block' }}>{item.errors.join(', ')}</span>
                                                )}
                                            </td>
                                            <td style={styles.td}>
                                                {item.productId ? (
                                                    <div>
                                                        <strong>{item.product.name}</strong><br/>
                                                        <span style={{ color: '#64748b', fontSize: '13px' }}>
                                                            {item.quantity} {item.product.unit || 'Adet'}
                                                        </span>
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td style={styles.td}>
                                                {item.productId ? (
                                                    <div>
                                                        <strong style={{ fontSize: '15px' }}>{item.price.toFixed(2)} {customers.find(c => c.id === selectedCustomerId)?.currency || 'TRY'}</strong>
                                                        {item.priceLabel && (
                                                            <div style={{ 
                                                                color: '#3b82f6', 
                                                                fontSize: '11px', 
                                                                marginTop: '2px', 
                                                                fontWeight: '600',
                                                                background: '#eff6ff',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                display: 'inline-block'
                                                            }}>{item.priceLabel}</div>
                                                        )}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td style={styles.td}>
                                                {item.productId && (
                                                    <div>
                                                        <strong>{item.total.toFixed(2)}</strong><br/>
                                                        <span style={{ color: '#64748b', fontSize: '12px' }}>{item.note}</span>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => {
                                    setImportStatus('config');
                                    setProcessedItems([]);
                                }}
                                style={{ ...styles.button, background: 'white', border: '1px solid #e2e8f0', color: '#475569' }}
                            >
                                <span role="img" aria-label="cancel">✖️</span> İptal Et ve Dosyayı Değiştir
                            </button>
                            <button
                                onClick={handleImport}
                                disabled={loading || processedItems.filter(o => o.status === 'valid').length === 0}
                                style={{
                                    ...styles.button,
                                    ...styles.primaryButton,
                                    ...(loading || processedItems.filter(o => o.status === 'valid').length === 0 ? styles.disabledButton : {})
                                }}
                            >
                                {loading ? 'Sipariş Oluşturuluyor...' : <><span role="img" aria-label="check">✅</span> {processedItems.filter(o => o.status === 'valid').length} Kalemlik Siparişi Aç</>}
                            </button>
                        </div>
                    </div>
                )}

                {error && (
                    <div style={{ marginTop: '20px', padding: '16px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', fontSize: '14px', border: '1px solid #fca5a5', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span role="img" aria-label="error">⚠️</span> {error}
                    </div>
                )}
            </div>
        </div>
    );
}

export default BulkOrderImport;
